<template>
  <div class="flex items-center">
    <label 
      class="label cursor-pointer flex items-center" 
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled" 
      :aria-checked="modelValue === nativeValue"
      :class="{ 'opacity-60 cursor-not-allowed': disabled, 'focus-visible:outline-offset-2 focus-visible:outline-primary rounded': !disabled }"
      @keydown.space.prevent="!disabled && updateValue()"
      @keydown.enter.prevent="!disabled && updateValue()"
    >
      <input
        :checked="modelValue === nativeValue"
        type="radio"
        class="radio"
        :class="[sizeClass, colorClass]"
        :disabled="disabled"
        :required="required"
        :name="name"
        :id="inputId"
        :value="nativeValue"
        @change="updateValue"
        aria-hidden="true"
      />
      <span class="ml-2" :id="`${inputId}-text`">
        <slot />
      </span>
    </label>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    default: null
  },
  nativeValue: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    required: true
  },
  id: {
    type: String,
    default: null
  },
  disabled: Boolean,
  required: Boolean,
  name: String,
  color: {
    type: String,
    default: "neutral",
    validator(value) {
      return [
        "neutral",
        "primary",
        "secondary",
        "accent",
        "info",
        "success",
        "warning",
        "error",
      ].includes(value);
    },
  },
  size: {
    type: String,
    default: "md",
    validator(value) {
      return ["xs", "sm", "md", "lg", "xl"].includes(value);
    },
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

// Generate a unique id if none is provided
const inputId = computed(() => props.id || `radio-${Math.random().toString(36).substring(2, 10)}`);

const updateValue = () => {
  if (!props.disabled) {
    emit('update:modelValue', props.nativeValue);
    emit('change', props.nativeValue);
  }
};

const sizeClass = computed(() => {
  switch (props.size) {
    case "xs": return "radio-xs";
    case "sm": return "radio-sm";
    case "md": return "radio-md";
    case "lg": return "radio-lg";
    case "xl": return "radio-xl";
    default: return "";
  }
});

const colorClass = computed(() => {
  switch (props.color) {
    case "neutral": return "radio-neutral";
    case "primary": return "radio-primary";
    case "secondary": return "radio-secondary";
    case "accent": return "radio-accent";
    case "info": return "radio-info";
    case "success": return "radio-success";
    case "warning": return "radio-warning";
    case "error": return "radio-error";
    default: return "";
  }
});
</script>
